import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PropertyImageRow {
  id: string;
  property_id: string;
  image_url: string;
  alt_text: string | null;
  caption: string | null;
  sort_order: number;
  is_featured: boolean;
}

export interface PropertyRow {
  id: string;
  title: string;
  slug: string;
  region: string;
  property_type: string | null;
  listing_status: string | null;
  price: string | null;
  price_numeric: number | null;
  location: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state_region: string | null;
  country: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  lot_size: string | null;
  short_description: string | null;
  description: string | null;
  investment_highlights: string[];
  amenities: string[];
  features: string[];
  hero_image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  is_featured: boolean;
  is_published: boolean;
  show_on_homepage: boolean;
  sort_order: number;
}

export interface PropertyWithImages extends PropertyRow {
  images: PropertyImageRow[];
}

interface UsePropertiesOptions {
  region?: string;
  featuredOnly?: boolean;
  homepageOnly?: boolean;
  slug?: string;
  withImages?: boolean;
  enabled?: boolean;
}

export function useProperties(opts: UsePropertiesOptions = {}) {
  const { region, featuredOnly, homepageOnly, slug, withImages, enabled = true } = opts;
  const [data, setData] = useState<PropertyWithImages[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      let q = supabase
        .from("properties")
        .select("*")
        .eq("is_published", true)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (region) q = q.eq("region", region);
      if (featuredOnly) q = q.eq("is_featured", true);
      if (homepageOnly) q = q.eq("show_on_homepage", true);
      if (slug) q = q.eq("slug", slug);

      const { data: rows, error: err } = await q;
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setData([]);
        setLoading(false);
        return;
      }
      const props = (rows ?? []) as unknown as PropertyRow[];
      if (!withImages || props.length === 0) {
        setData(props.map((p) => ({ ...p, images: [] })));
        setLoading(false);
        return;
      }
      const ids = props.map((p) => p.id);
      const { data: imgs, error: imgErr } = await supabase
        .from("property_images")
        .select("*")
        .in("property_id", ids)
        .order("sort_order", { ascending: true });
      if (cancelled) return;
      if (imgErr) {
        setError(imgErr.message);
        setData(props.map((p) => ({ ...p, images: [] })));
      } else {
        const byId = new Map<string, PropertyImageRow[]>();
        ((imgs ?? []) as unknown as PropertyImageRow[]).forEach((im) => {
          const arr = byId.get(im.property_id) ?? [];
          arr.push(im);
          byId.set(im.property_id, arr);
        });
        setData(props.map((p) => ({ ...p, images: byId.get(p.id) ?? [] })));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, featuredOnly, homepageOnly, slug, withImages, enabled]);

  return { data, loading, error };
}

export default useProperties;
